/**
 * 2026-05-30 — Player self-signup landing page.
 *
 * Route: /me/player
 *
 * Combines the PlayerProfile editor with a full stats panel:
 *   • Personal career rollup (batting + bowling)
 *   • Per-team aggregates (batting + bowling) — "stats for each team
 *     I played for"
 *   • Vs-opposition aggregates (batting + bowling) — "stats vs each
 *     team I played against"
 *   • Wagon wheel (scoring shots) + pitch map (deliveries bowled)
 *   • Phase splits (PP / Middle / Death)
 *   • Top matchups (most balls faced vs each bowler)
 *   • Recent video clips
 *   • Recent form (last innings list)
 *
 * Players signed up via the "Player" branch of /signup land here.
 * The same router gate (RequireNotPlayer) bounces them back here
 * from any other authenticated route.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";

import { getMyPlayerProfile } from "../../api/coredata.api";
import apiAxios from "../../api/axios";
import HighlightsPlayer from "../../components/players/HighlightsPlayer";


function StatBox({ label, value, sub }) {
  return (
    <div className="bg-bg-secondary rounded-lg p-3 border border-bg-main">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</div>
      <div className="text-lg font-semibold text-text-primary mt-0.5">
        {value ?? "—"}
      </div>
      {sub && <div className="text-[10px] text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}


function TeamRollupTable({ title, rows, kind, onPickTeam, innings: inningsByTeam, onPickInnings }) {
  // For batting tables we group by team and expand to show each
  // individual innings vs that team (founder ask 2026-06-02: 2 matches
  // vs the same opposition should display as 2 separate clickable rows,
  // not lumped into one aggregate line). `inningsByTeam` is a map keyed
  // by team_id → array of innings rows (only used when kind="bat").
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpand = (teamId) => setExpandedId((prev) => prev === teamId ? null : teamId);
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
        <h3 className="text-sm font-semibold text-text-primary mb-2">{title}</h3>
        <p className="text-xs text-text-secondary">No data yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
      <h3 className="text-sm font-semibold text-text-primary mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-text-secondary">
            <tr className="border-b border-bg-secondary">
              <th className="text-left py-1.5 pr-2">Team</th>
              <th className="text-right py-1.5 px-2">Inns</th>
              {kind === "bat" ? (
                <>
                  <th className="text-right py-1.5 px-2">Runs</th>
                  <th className="text-right py-1.5 px-2">Balls</th>
                  <th className="text-right py-1.5 px-2">4s</th>
                  <th className="text-right py-1.5 px-2">6s</th>
                  <th className="text-right py-1.5 px-2">SR</th>
                  <th className="text-right py-1.5 px-2">Avg</th>
                </>
              ) : (
                <>
                  <th className="text-right py-1.5 px-2">Overs</th>
                  <th className="text-right py-1.5 px-2">Runs</th>
                  <th className="text-right py-1.5 px-2">Wkts</th>
                  <th className="text-right py-1.5 px-2">Econ</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const teamInnings = (inningsByTeam || {})[r.team_id] || [];
              const isExpandable = kind === "bat" && teamInnings.length > 0;
              const isOpen = expandedId === r.team_id;
              return (
              <React.Fragment key={r.team_id}>
              <tr
                className={`border-b border-bg-secondary/40 last:border-0 ${
                  isExpandable
                    ? "cursor-pointer hover:bg-bg-secondary/40"
                    : (onPickTeam ? "cursor-pointer hover:bg-bg-secondary/40" : "")
                }`}
                onClick={
                  isExpandable
                    ? () => toggleExpand(r.team_id)
                    : (onPickTeam ? () => onPickTeam(r) : undefined)
                }
                title={
                  isExpandable
                    ? `Expand to see each innings vs ${r.team_name}`
                    : (onPickTeam ? "Click to watch boundary highlights vs this team" : undefined)
                }
              >
                <td className="py-1.5 pr-2 text-text-primary">
                  {isExpandable && (
                    <span className="text-text-secondary mr-1 inline-block w-3">
                      {isOpen ? "▾" : "▸"}
                    </span>
                  )}
                  {r.team_name}
                  {isExpandable && teamInnings.length > 1 && (
                    <span className="ml-1 text-[10px] text-text-secondary">
                      ({teamInnings.length} innings)
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-right">{r.innings}</td>
                {kind === "bat" ? (
                  <>
                    <td className="py-1.5 px-2 text-right font-mono">{r.runs}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.balls}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-emerald-300">{r.fours ?? 0}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-purple-300">{r.sixes ?? 0}</td>
                    <td className="py-1.5 px-2 text-right font-mono">
                      {r.strike_rate ?? "—"}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">
                      {r.average ?? "—"}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-1.5 px-2 text-right font-mono">{r.overs}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.runs}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.wickets}</td>
                    <td className="py-1.5 px-2 text-right font-mono">
                      {r.economy ?? "—"}
                    </td>
                  </>
                )}
              </tr>
              {/* Expanded per-innings rows for this team. Each row =
                  one specific innings. Click → highlights for that
                  match. */}
              {isExpandable && isOpen && teamInnings.map((inn, idx) => (
                <tr
                  key={`${r.team_id}-${inn.match_id}-${idx}`}
                  className="bg-bg-secondary/30 border-b border-bg-secondary/30 cursor-pointer hover:bg-bg-secondary/60"
                  onClick={() => onPickInnings?.(inn)}
                  title="Watch boundary highlights from this innings"
                >
                  <td className="py-1.5 pr-2 pl-6 text-text-secondary text-[11px]">
                    ↳ {inn.match_date || "—"}
                    {inn.innings_number ? ` · Inn ${inn.innings_number}` : ""}
                  </td>
                  <td className="py-1.5 px-2 text-right text-[11px]">1</td>
                  {kind === "bat" ? (
                    <>
                      <td className="py-1.5 px-2 text-right font-mono text-[11px]">{inn.runs ?? 0}{inn.is_out ? "" : "*"}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-[11px]">{inn.balls ?? 0}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-emerald-300 text-[11px]">{inn.fours ?? 0}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-purple-300 text-[11px]">{inn.sixes ?? 0}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-[11px]">{inn.strike_rate ?? "—"}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-[11px] text-text-secondary">—</td>
                    </>
                  ) : null}
                </tr>
              ))}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function TeamsRepresentedPanel({ byTeamBatting = [], byTeamBowling = [] }) {
  // Merge batting + bowling rows by team_id so each chip can pull both
  // sides on click. Team appears even if the player only batted (or only
  // bowled) for them.
  const teams = useMemo(() => {
    const map = new Map();
    for (const r of byTeamBatting || []) {
      map.set(r.team_id, { team_id: r.team_id, team_name: r.team_name, bat: r, bowl: null });
    }
    for (const r of byTeamBowling || []) {
      const existing = map.get(r.team_id);
      if (existing) existing.bowl = r;
      else map.set(r.team_id, { team_id: r.team_id, team_name: r.team_name, bat: null, bowl: r });
    }
    return Array.from(map.values());
  }, [byTeamBatting, byTeamBowling]);

  const [selectedId, setSelectedId] = useState(null);

  // Auto-select the first team once data lands so the panel never sits
  // empty when the player has only one team.
  useEffect(() => {
    if (teams.length > 0 && !selectedId) {
      setSelectedId(teams[0].team_id);
    }
    // If the previously-selected team disappears (stats refetch), reset.
    if (selectedId && !teams.find((t) => t.team_id === selectedId)) {
      setSelectedId(teams[0]?.team_id || null);
    }
  }, [teams, selectedId]);

  if (teams.length === 0) {
    return (
      <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
        <h3 className="text-sm font-semibold text-text-primary mb-2">
          Teams represented
        </h3>
        <p className="text-xs text-text-secondary">
          No team data yet. Once a scorer records you for a team, that
          team will appear here.
        </p>
      </div>
    );
  }

  const selected = teams.find((t) => t.team_id === selectedId) || teams[0];

  return (
    <div className="space-y-3">
      {/* Chip strip — one chip per team the player has represented. */}
      <div className="flex flex-wrap gap-2">
        {teams.map((t) => {
          const isOn = selected && t.team_id === selected.team_id;
          return (
            <button
              key={t.team_id}
              type="button"
              onClick={() => setSelectedId(t.team_id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                isOn
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-bg-secondary text-text-primary border-bg-secondary hover:bg-bg-main"
              }`}
            >
              {t.team_name}
            </button>
          );
        })}
      </div>

      {/* Detail — batting + bowling stats FOR the selected team only. */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
          <h3 className="text-sm font-semibold text-text-primary mb-3">
            Batting for {selected.team_name}
          </h3>
          {selected.bat ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              <StatBox label="Inns" value={selected.bat.innings} />
              <StatBox label="Runs" value={selected.bat.runs} />
              <StatBox label="Balls" value={selected.bat.balls} />
              <StatBox label="4s" value={selected.bat.fours ?? 0} />
              <StatBox label="6s" value={selected.bat.sixes ?? 0} />
              <StatBox label="Outs" value={selected.bat.outs ?? 0} />
              <StatBox label="SR" value={selected.bat.strike_rate ?? "—"} />
              <StatBox label="Avg" value={selected.bat.average ?? "—"} />
            </div>
          ) : (
            <p className="text-xs text-text-secondary">Didn't bat for this team.</p>
          )}
        </div>
        <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
          <h3 className="text-sm font-semibold text-text-primary mb-3">
            Bowling for {selected.team_name}
          </h3>
          {selected.bowl ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              <StatBox label="Inns" value={selected.bowl.innings} />
              <StatBox label="Overs" value={selected.bowl.overs} />
              <StatBox label="Wkts" value={selected.bowl.wickets} />
              <StatBox label="Runs" value={selected.bowl.runs} />
              <StatBox label="Econ" value={selected.bowl.economy ?? "—"} />
              <StatBox label="Avg" value={selected.bowl.average ?? "—"} />
            </div>
          ) : (
            <p className="text-xs text-text-secondary">Didn't bowl for this team.</p>
          )}
        </div>
      </div>
    </div>
  );
}


function WagonWheelPanel({ points }) {
  // Broadcast-style wagon wheel — green striped field, yellow (1-3) /
  // blue (4) / red (6) shot lines from the pitch, 8 sector totals
  // around the outer ring, and a runs-breakdown row beneath. Mirrors
  // the layout the founder uses as a visual reference.
  // Coords arrive normalised to [-1, 1] from the backend.
  const R = 88, CX = 100, CY = 100;
  const PATTERN_ID = "ww-field-stripes-me";
  const pts = points || [];

  // 1s/2s/3s/4s/6s counts + 8-sector totals.
  const counts = { "1": 0, "2": 0, "3": 0, "4": 0, "6": 0 };
  let totalRuns = 0;
  const sectorRuns = new Array(8).fill(0);
  for (const p of pts) {
    const r = p.runs || 0;
    totalRuns += r;
    if (counts[String(r)] !== undefined) counts[String(r)] += 1;
    const x = p.x ?? 0;
    const y = p.y ?? 0;
    // Angle measured clockwise from "north" (top of field, where the
    // bowler runs in). atan2(x, -y) gives 0 at top, +π/2 at right.
    let theta = Math.atan2(x, -y);
    if (theta < 0) theta += 2 * Math.PI;
    // Shift by π/8 (22.5°) so sector 0 is centred at north, not on
    // a boundary line.
    const idx = Math.floor(((theta + Math.PI / 8) % (2 * Math.PI)) / (Math.PI / 4));
    sectorRuns[idx] += r;
  }

  // Pixel positions for the 8 sector labels, just outside the boundary.
  const labelR = R + 14;
  const sectorLabels = Array.from({ length: 8 }, (_, i) => {
    // i=0 north, 1=NE, 2=E, 3=SE, 4=S, 5=SW, 6=W, 7=NW.
    const ang = (i * Math.PI) / 4 - Math.PI / 2; // SVG: 0rad = right; shift to start at top
    return {
      idx: i,
      x: CX + Math.cos(ang) * labelR,
      y: CY + Math.sin(ang) * labelR,
      runs: sectorRuns[i],
    };
  });

  const colorFor = (r) =>
    r === 6 ? "rgb(220, 38, 38)" :        // red-600
    r === 4 ? "rgb(37, 99, 235)" :        // blue-600
    r >= 1 ? "rgb(234, 179, 8)"  :        // yellow-500
            "rgba(255,255,255,0.25)";

  return (
    <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
      <h3 className="text-sm font-semibold text-text-primary mb-3">
        Wagon wheel · scoring shots
      </h3>
      <div className="flex items-center justify-center">
        <svg viewBox="0 0 224 224" className="w-full max-w-md">
          <defs>
            {/* Diagonal mowed-field stripes — alternating greens. */}
            <pattern id={PATTERN_ID} patternUnits="userSpaceOnUse"
                     width="14" height="14" patternTransform="rotate(45)">
              <rect width="14" height="14" fill="rgb(58, 138, 64)" />
              <rect width="7"  height="14" fill="rgb(72, 156, 78)" />
            </pattern>
          </defs>

          {/* Boundary circle filled with stripe pattern. */}
          <circle cx={CX + 12} cy={CY + 12} r={R}
                  fill={`url(#${PATTERN_ID})`}
                  stroke="rgba(255,255,255,0.9)" strokeWidth="1.4" />
          {/* 30-yard inner circle */}
          <circle cx={CX + 12} cy={CY + 12} r={R * 0.5}
                  fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="0.8" />

          <g transform="translate(12, 12)">
            {/* Pitch rectangle (centre strip) — vertical, slightly
                taller than wide to suggest the 22-yard pitch. */}
            <rect x={CX - 4} y={CY - 16} width="8" height="32"
                  fill="rgba(245, 230, 196, 0.85)"
                  stroke="rgba(255,255,255,0.6)" strokeWidth="0.4" />
            {/* Stumps as small lines */}
            <line x1={CX - 3} y1={CY - 16} x2={CX + 3} y2={CY - 16}
                  stroke="rgba(255,255,255,0.9)" strokeWidth="0.6" />
            <line x1={CX - 3} y1={CY + 16} x2={CX + 3} y2={CY + 16}
                  stroke="rgba(255,255,255,0.9)" strokeWidth="0.6" />

            {/* Shot lines from pitch centre. Render lower-value lines
                first so 4s/6s sit on top. */}
            {[...pts].sort((a, b) => (a.runs || 0) - (b.runs || 0)).map((p, idx) => {
              const x = CX + (p.x ?? 0) * R;
              const y = CY + (p.y ?? 0) * R;
              const color = colorFor(p.runs || 0);
              const w = (p.runs === 6) ? 1.4 : (p.runs === 4 ? 1.2 : 0.9);
              return (
                <line key={idx}
                      x1={CX} y1={CY} x2={x} y2={y}
                      stroke={color} strokeWidth={w}
                      strokeLinecap="round"
                      opacity={0.85} />
              );
            })}
          </g>

          {/* Sector run totals — placed just outside the boundary. */}
          {sectorLabels.map((s) => (
            s.runs > 0 ? (
              <text key={s.idx}
                    x={s.x + 12} y={s.y + 12}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="9"
                    fontWeight="600"
                    fill="rgba(255,255,255,0.95)">
                {s.runs} Runs
              </text>
            ) : null
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-3 mt-3 text-[10px]">
        <span className="inline-flex items-center gap-1 text-text-secondary">
          <span className="inline-block w-3 h-1 bg-yellow-500 rounded-sm" />
          1-3 runs
        </span>
        <span className="inline-flex items-center gap-1 text-text-secondary">
          <span className="inline-block w-3 h-1 bg-blue-600 rounded-sm" />
          4 runs
        </span>
        <span className="inline-flex items-center gap-1 text-text-secondary">
          <span className="inline-block w-3 h-1 bg-red-600 rounded-sm" />
          6 runs
        </span>
      </div>

      {/* Runs / 1s / 2s / 3s / 4s / 6s breakdown row. */}
      <div className="mt-3 grid grid-cols-6 gap-1 text-center text-[10px]">
        {[
          ["Runs", totalRuns],
          ["1s", counts["1"]],
          ["2s", counts["2"]],
          ["3s", counts["3"]],
          ["4s", counts["4"]],
          ["6s", counts["6"]],
        ].map(([k, v]) => (
          <div key={k} className="bg-bg-secondary/60 rounded py-1 border border-bg-secondary">
            <div className="uppercase tracking-wider text-text-secondary">{k}</div>
            <div className="font-mono text-sm text-text-primary mt-0.5">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function PitchMapPanel({ points }) {
  const ROWS = ["Beamer", "Full Toss", "Yorker", "Full", "Length ball",
                "Back of a length", "Short", "Half tracker"];
  const COLS = ["WOO", "OO", "OS", "MS", "LS", "DL", "WDL"];
  const grid = ROWS.map(() => COLS.map(() => ({ count: 0, wickets: 0 })));
  for (const p of (points || [])) {
    const r = ROWS.indexOf(p.length);
    const c = COLS.indexOf(p.line);
    if (r >= 0 && c >= 0) {
      grid[r][c].count += 1;
      if (p.wicket) grid[r][c].wickets += 1;
    }
  }
  const max = Math.max(1, ...grid.flat().map((g) => g.count));
  return (
    <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
      <h3 className="text-sm font-semibold text-text-primary mb-3">
        Pitch map · deliveries bowled
      </h3>
      <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-1 text-[9px]">
        <div></div>
        {COLS.map((c) => (
          <div key={c} className="text-center text-text-secondary font-mono">{c}</div>
        ))}
        {ROWS.map((row, r) => (
          <div key={row} className="contents">
            <div className="text-text-secondary py-1">{row}</div>
            {COLS.map((_, c) => {
              const g = grid[r][c];
              const intensity = g.count / max;
              const bg = g.wickets > 0
                ? `rgba(239, 68, 68, ${0.3 + intensity * 0.7})`
                : `rgba(59, 130, 246, ${intensity * 0.7})`;
              return (
                <div
                  key={c}
                  className="h-6 flex items-center justify-center text-[9px] text-white rounded-sm"
                  style={{ backgroundColor: g.count > 0 ? bg : "rgba(255,255,255,0.04)" }}
                  title={`${row} / ${COLS[c]}: ${g.count} balls, ${g.wickets} wickets`}
                >
                  {g.count > 0 ? g.count : ""}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-text-secondary mt-2">
        Red = wickets · Blue = runs conceded
      </p>
    </div>
  );
}


function PhaseSplits({ phases }) {
  const ph = phases || {};
  const order = [
    ["PP",    "Powerplay"],
    ["MID",   "Middle"],
    ["DEATH", "Death"],
  ];
  return (
    <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
      <h3 className="text-sm font-semibold text-text-primary mb-3">
        Phase splits (batting)
      </h3>
      <div className="grid grid-cols-3 gap-2 text-center">
        {order.map(([k, label]) => {
          const p = ph[k] || {};
          return (
            <div key={k} className="bg-bg-secondary rounded p-2 border border-bg-main">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary">
                {label}
              </div>
              <div className="text-base font-semibold font-mono text-text-primary mt-1">
                {p.runs ?? 0}/{p.balls ?? 0}
              </div>
              <div className="text-[10px] text-emerald-400 font-mono">
                SR {p.sr ?? "—"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function VideosGrid({ videos }) {
  if (!videos || videos.length === 0) {
    return (
      <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
        <h3 className="text-sm font-semibold text-text-primary mb-2">My videos</h3>
        <p className="text-xs text-text-secondary">
          No video clips yet. Once a scorer records you on the ball,
          the clips will appear here.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
      <h3 className="text-sm font-semibold text-text-primary mb-3">
        My videos ({videos.length})
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {videos.map((v) => (
          <div key={v.ball_id}
               className="bg-bg-secondary rounded overflow-hidden border border-bg-main">
            {v.video_url ? (
              <video
                src={v.video_url}
                controls
                preload="metadata"
                className="w-full aspect-video bg-black"
              />
            ) : (
              <div className="w-full aspect-video bg-black flex items-center justify-center text-[10px] text-text-secondary">
                Clip unavailable
              </div>
            )}
            <div className="px-2 py-1.5 text-[11px] text-text-secondary flex items-center justify-between">
              <span>
                {v.role === "BAT" ? "Batting" : "Bowling"} · vs {v.vs}
              </span>
              <span className="font-mono text-text-primary">
                {v.runs}r{v.wicket ? " · W" : ""}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function LastInningsList({ innings, onPickInnings }) {
  if (!innings || innings.length === 0) {
    return (
      <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
        <h3 className="text-sm font-semibold text-text-primary mb-2">Recent form</h3>
        <p className="text-xs text-text-secondary">No innings recorded yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
      <h3 className="text-sm font-semibold text-text-primary mb-3">
        Recent form ({innings.length} innings)
        <span className="ml-2 text-[10px] font-normal text-text-secondary">
          · tap a row to watch the boundary highlights
        </span>
      </h3>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {innings.map((i, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onPickInnings?.(i)}
            className="w-full flex items-center justify-between text-xs py-1 px-2 hover:bg-bg-secondary/60 rounded text-left"
          >
            <span className="text-text-secondary">
              {i.match_date || "—"} · vs {i.vs}
            </span>
            <span className="font-mono">
              {i.runs ?? 0}
              {!i.is_out && "*"} ({i.balls ?? 0})
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}


export default function MyPlayerHub() {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Boundary-highlights modal. `filter` is one of:
  //   { match_id: <uuid> }  — single innings of a single match
  //   { vs_team:  <uuid> }  — all innings vs that opposition
  const [highlights, setHighlights] = useState(null);  // { filter, title } | null

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        // 1. Get the player's canonical row (auto-creates on first hit).
        const { data: prof } = await getMyPlayerProfile();
        setProfile(prof);
        // 2. Pull full stats payload (career + last innings + per-team
        //    + vs-opposition + matchups + phases + wagon/pitch + videos).
        if (prof?.id) {
          try {
            const r = await apiAxios.get(`/api/v1/players/${prof.id}/profile/`);
            setStats(r.data);
          } catch {
            // Stats endpoint requires tenant scope; player accounts may
            // not have one yet (no matches played). Fall back to empty —
            // every section below handles a null `stats` cleanly.
            setStats(null);
          }
        }
      } catch (e) {
        // Soft-fail: still render the page shell with whatever profile
        // info we have (or none) plus the editor link. A network blip
        // shouldn't strand the player on a flat error page.
        setError(e?.response?.data?.detail || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group last_innings entries by opposition team_id so the Vs Opposition
  // table can expand each team into its individual innings rows.
  //
  // MUST stay ABOVE the `if (loading) return …` early return — React
  // requires hook order to be stable across renders. Hoisting this
  // memo above the early return prevents React error #310 ("rendered
  // more hooks than during the previous render") on initial paint.
  const inningsByOpponent = useMemo(() => {
    const map = {};
    for (const inn of (stats?.last_innings || [])) {
      const k = inn.vs_team_id;
      if (!k) continue;
      (map[k] = map[k] || []).push(inn);
    }
    return map;
  }, [stats?.last_innings]);

  if (loading) {
    return (
      <div className="p-8 text-text-secondary">Loading your profile…</div>
    );
  }

  const fullName = `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();
  const batting = stats?.batting || {};
  const bowling = stats?.bowling || {};
  const hasAnyStats =
    (batting.innings || 0) > 0 ||
    (bowling.innings || 0) > 0 ||
    ((stats?.wagon_points || []).length) > 0;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-text-primary break-words">
            {fullName || "Your player profile"}
          </h1>
          <p className="text-sm text-text-secondary mt-1 break-all">
            {profile?.role || "Player"}
            {profile?.mobile ? ` · ${profile.mobile}` : ""}
          </p>
        </div>
        <div className="self-start sm:self-auto flex shrink-0 items-center gap-2">
          {/* 2026-06-04 — surface a top-level Highlights trigger.
              Empty filter → modal aggregates every boundary across
              the player's career. */}
          {profile?.id && (
            <button
              onClick={() => setHighlights({
                filter: {},
                title: `${fullName || "Your"} · Boundary highlights`,
              })}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-sm font-semibold hover:bg-emerald-500/25 whitespace-nowrap"
              title="Watch every boundary you've hit"
            >
              <PlayCircle className="w-4 h-4" />
              Highlights
            </button>
          )}
          <Link
            to="/account/player-profile"
            className="inline-flex px-4 py-2 rounded-lg bg-button-primary text-white text-sm hover:opacity-90 whitespace-nowrap"
          >
            Edit profile
          </Link>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          {error} · You can still edit your details above.
        </div>
      )}
      {!hasAnyStats && !error && (
        <div className="rounded-lg border border-bg-secondary bg-bg-main px-4 py-3 text-sm text-text-secondary">
          Your stats and videos will appear here once a scorer records
          you in a live match. Share your registered mobile number with
          them so they can add you to their squad.
        </div>
      )}

      {/* Personal career */}
      <section>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Personal career
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <StatBox label="Innings (bat)" value={batting.innings} />
          <StatBox label="Runs" value={batting.runs} />
          <StatBox label="Strike rate" value={batting.strike_rate} />
          <StatBox label="Average" value={batting.average} />
          <StatBox label="Innings (bowl)" value={bowling.innings} />
          <StatBox
            label="Wickets"
            value={bowling.wickets}
            sub={bowling.overs ? `${bowling.overs} ov` : null}
          />
          <StatBox label="Economy" value={bowling.economy} />
          <StatBox label="Bowling avg" value={bowling.average} />
        </div>
      </section>

      {/* Teams represented — click a chip to drill into that team's
          batting + bowling. Auto-selects the first team on first load
          so the panel is never empty when the player has only one. */}
      <section>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Teams represented
        </h2>
        <TeamsRepresentedPanel
          byTeamBatting={stats?.by_team_batting}
          byTeamBowling={stats?.by_team_bowling}
        />
      </section>

      {/* Vs opposition — aggregated row per opponent. Click to expand
          and see each individual innings vs that team (founder ask:
          2 matches vs same opponent show separately). Each innings
          row is itself clickable → boundary highlights for that match. */}
      <section>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Vs opposition
          <span className="ml-2 text-[10px] font-normal text-text-secondary normal-case tracking-normal">
            · tap a row to see each innings, then a specific innings for highlights
          </span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <TeamRollupTable
            title="Batting"
            rows={stats?.vs_team_batting}
            kind="bat"
            innings={inningsByOpponent}
            onPickInnings={(inn) => setHighlights({
              filter: { match_id: inn.match_id },
              title: `Boundaries · vs ${inn.vs}${inn.match_date ? ` · ${inn.match_date}` : ""}`,
            })}
          />
          <TeamRollupTable title="Bowling" rows={stats?.vs_team_bowling} kind="bowl" />
        </div>
      </section>

      {/* Wagon wheel + pitch map side-by-side */}
      <section>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Where the runs went
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <WagonWheelPanel points={stats?.wagon_points} />
          <PitchMapPanel points={stats?.pitch_points} />
        </div>
      </section>

      {/* Phase splits */}
      <section>
        <PhaseSplits phases={stats?.phases} />
      </section>

      {/* Videos */}
      <section>
        <VideosGrid videos={stats?.videos} />
      </section>

      {/* Recent form — click a row to play boundary highlights from
          that specific innings. */}
      <section>
        <LastInningsList
          innings={stats?.last_innings}
          onPickInnings={(i) => setHighlights({
            filter: { match_id: i.match_id },
            title: `Boundaries · vs ${i.vs}${i.match_date ? ` · ${i.match_date}` : ""}`,
          })}
        />
      </section>

      {highlights && profile?.id && (
        <HighlightsPlayer
          playerId={profile.id}
          filter={highlights.filter}
          title={highlights.title}
          onClose={() => setHighlights(null)}
        />
      )}
    </div>
  );
}
